import React, { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { isIOS, isStandalone } from "@/lib/push";
import { Download, X, Share, Plus, MoreVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DISMISS_KEY = "kn_pwa_dismissed";

function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (isIOS()) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

export const InstallAppBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    if (isStandalone()) {
      setHidden(true);
      return;
    }
    if (localStorage.getItem(DISMISS_KEY) === "1") {
      setHidden(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setHidden(true);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setHidden(true);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") setHidden(true);
      } catch (_) {}
      setDeferred(null);
      return;
    }
    setHelpOpen(true);
  };

  if (hidden) return null;

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="kn-fade-up flex items-center gap-3 rounded-2xl bg-slate-900 text-white p-3 pr-2 shadow-lg"
      >
        <img
          src={`${API}/pwa/icon`}
          alt="KoupoNow"
          className="h-11 w-11 rounded-xl shrink-0 bg-white/10 object-cover"
        />
        <div className="min-w-0 flex-1">
          <p className="font-extrabold text-sm leading-tight">Installa l'app KoupoNow</p>
          <p className="text-[12px] text-slate-300 leading-tight mt-0.5">
            Aggiungila alla schermata Home e aprila come un'app.
          </p>
        </div>
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          className="shrink-0 h-9 px-3.5 rounded-xl bg-[#FFE066] hover:bg-[#FFD84A] text-slate-900 font-bold text-sm flex items-center gap-1.5 transition-colors"
        >
          <Download className="h-4 w-4" /> Installa
        </button>
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Chiudi"
          className="shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="rounded-2xl" data-testid="pwa-ios-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src={`${API}/pwa/icon`} alt="" className="h-7 w-7 rounded-lg" />
              Installa KoupoNow
            </DialogTitle>
          </DialogHeader>

          {platform === "ios" && (
            <div className="space-y-3 pt-1">
              <p className="text-sm text-slate-600">Su iPhone bastano due passaggi da Safari:</p>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-7 w-7 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center justify-center">1</span>
                  <span className="text-sm text-slate-700 pt-0.5">
                    Tocca l'icona <b className="inline-flex items-center gap-1"><Share className="h-4 w-4" /> Condividi</b> in basso nella barra di Safari.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-7 w-7 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center justify-center">2</span>
                  <span className="text-sm text-slate-700 pt-0.5">
                    Scorri e scegli <b className="inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Aggiungi alla schermata Home</b>, poi conferma.
                  </span>
                </li>
              </ol>
            </div>
          )}

          {platform === "android" && (
            <div className="space-y-3 pt-1">
              <p className="text-sm text-slate-600">Su Android, da Chrome:</p>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-7 w-7 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center justify-center">1</span>
                  <span className="text-sm text-slate-700 pt-0.5">
                    Tocca il menu <b className="inline-flex items-center gap-1"><MoreVertical className="h-4 w-4" /> (tre puntini)</b> in alto a destra.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-7 w-7 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center justify-center">2</span>
                  <span className="text-sm text-slate-700 pt-0.5">
                    Scegli <b>Installa app</b> (o <b>Aggiungi alla schermata Home</b>) e conferma.
                  </span>
                </li>
              </ol>
            </div>
          )}

          {platform === "desktop" && (
            <div className="space-y-3 pt-1">
              <p className="text-sm text-slate-600">Dal computer, con Chrome o Edge:</p>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-7 w-7 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center justify-center">1</span>
                  <span className="text-sm text-slate-700 pt-0.5">
                    Clicca l'icona <b className="inline-flex items-center gap-1"><Download className="h-4 w-4" /> Installa</b> a destra nella barra degli indirizzi.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 h-7 w-7 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center justify-center">2</span>
                  <span className="text-sm text-slate-700 pt-0.5">
                    In alternativa apri il menu del browser e scegli <b>Installa KoupoNow</b>.
                  </span>
                </li>
              </ol>
              <p className="text-xs text-slate-400">Suggerimento: l'esperienza migliore è dallo smartphone, dove l'app viene aggiunta alla schermata Home.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppBanner;
